/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Notification Service
 * ============================================================================
 *
 * Service for fetching notifications from the backend.
 * Supports role-based notification filtering.
 *
 * ============================================================================
 */

import { get } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';
import { NotificationSummary, emptyNotificationSummary } from '@/types/notification.types';

const BASE_URL = '/notifications';

/**
 * Notification service for fetching user notifications.
 */
export const notificationService = {
  /**
   * Get notification summary for the current user.
   * Notifications are filtered based on the user's role.
   *
   * @returns NotificationSummary with attention and activity items
   */
  async getNotifications(): Promise<NotificationSummary> {
    try {
      const response = await get<ApiResponse<NotificationSummary>>(BASE_URL);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return emptyNotificationSummary;
    }
  },

  /**
   * Get notification count for badge display.
   * This is a lightweight endpoint for polling.
   *
   * @returns Total notification count
   */
  async getNotificationCount(): Promise<number> {
    try {
      const response = await get<ApiResponse<number>>(`${BASE_URL}/count`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
      return 0;
    }
  },

  /**
   * Get list of registered notification providers.
   * Useful for debugging.
   *
   * @returns Array of provider IDs
   */
  async getProviders(): Promise<string[]> {
    try {
      const response = await get<ApiResponse<string[]>>(`${BASE_URL}/providers`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch notification providers:', error);
      return [];
    }
  },
};

export default notificationService;
